import { InlineAudioPlayer } from '@core/components';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import type { GraphQLAuditItem } from '@internal-audit/domain/graphql-types';
import { Text, TextArea } from '@soteria/design-system-ve';

import { LikeDislikeButtons } from '../../LikeDislikeButtons/LikeDislikeButtons';
import * as styles from '../AuditDecisionPanel.css';
import { useTranscriptionAudio } from '../hooks/useTranscriptionAudio';
import * as spacingStyles from '../spacing.css';

interface TranscriptionSectionProps {
  selectedItem: GraphQLAuditItem | null;
  transcriptionCorrect: boolean | null;
  correctedTranscription: string;
  isSessionCompleted: boolean;
  onTranscriptionCorrectChange: (value: boolean | null) => void;
  onCorrectedTranscriptionChange: (value: string) => void;
  getTranscription: (item: GraphQLAuditItem | null) => string;
}

export function TranscriptionSection({
  selectedItem,
  transcriptionCorrect,
  correctedTranscription,
  isSessionCompleted,
  onTranscriptionCorrectChange,
  onCorrectedTranscriptionChange,
  getTranscription,
}: TranscriptionSectionProps) {
  // Get system configuration including mediaServiceUrl
  const { mediaServiceUrl } = useSystemConfig();

  // Audio player logic using custom hook to reduce complexity
  const { audioUrl, shouldShowAudioPlayer } = useTranscriptionAudio(selectedItem);

  const handleChange = (value: boolean) => {
    // If clicking the same button that's already selected, deselect it (set to null)
    const newValue = transcriptionCorrect === value ? null : value;

    onTranscriptionCorrectChange(newValue);
  };

  return (
    <div className={spacingStyles.sectionContainer}>
      <Text className={styles.sectionTitle} id="transcription-label">
        Transcription
      </Text>

      <div className={styles.controlRow}>
        <span className={styles.assessmentQuestion}>Do you agree with the transcription?</span>
        <LikeDislikeButtons disabled={isSessionCompleted} name="transcription" value={transcriptionCorrect} onChange={handleChange} />
      </div>

      <div className={styles.transcriptionPreview}>
        <span className={styles.label}>Original:</span>
        <p className={styles.originalText}>{getTranscription(selectedItem) || 'No transcription available'}</p>

        {/* Audio Player - Inside the dark content area, only show for audio content */}
        {shouldShowAudioPlayer && audioUrl && selectedItem && (
          <div className={styles.audioPlayerSection}>
            <InlineAudioPlayer
              audioId={selectedItem.id}
              audioUrl={audioUrl}
              mediaServiceUrl={mediaServiceUrl}
              transcription={selectedItem.contentSnapshot?.transcription}
            />
          </div>
        )}
      </div>

      {transcriptionCorrect === false && (
        <div className={styles.transcriptionCorrectionField}>
          <TextArea
            disabled={isSessionCompleted}
            placeholder="Enter the corrected transcription..."
            rows={4}
            title="Corrected Transcription"
            value={correctedTranscription}
            onChange={e => onCorrectedTranscriptionChange(e.target.value)}
          />
        </div>
      )}
    </div>
  );
}
